import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { CreditCard, Loader2, AlertTriangle, CheckCircle2, Lock, Receipt, RefreshCw } from 'lucide-react';
import { apiErrorMessage } from '@/lib/apiError';
import { formatMoney as money } from '@/lib/api';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const OFFICE_PHONE = '+1 619 930 1074';

const day = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');
const authHeaders = (token) => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` });

/**
 * SelleGard credit, merchant side.
 *
 * SelleGard shipping runs on credit for authorized merchants only: a limit,
 * what is owed, and a weekly itemized invoice that is paid in full or the
 * credit goes on hold. This card says which of those states the merchant is
 * in, and lets them pay an open invoice by card.
 *
 * When Stripe sends the merchant back here it adds ?invoice=…&session_id=…,
 * and the card asks the backend how that payment ended. The webhook settles
 * it too; the poll is what makes the screen show it without waiting.
 */
export default function SelleGardCreditCard({ token }) {
  const [credit, setCredit] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [paying, setPaying] = useState(null);
  const [autopayBusy, setAutopayBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API}/webship/credit/me`, { headers: authHeaders(token) }),
        fetch(`${API}/webship/credit/invoices`, { headers: authHeaders(token) }),
      ]);
      const [c, inv] = await Promise.all([r1.json(), r2.json()]);
      if (!r1.ok) throw new Error(apiErrorMessage(c, 'Could not load your credit.'));
      if (!r2.ok) throw new Error(apiErrorMessage(inv, 'Could not load your invoices.'));
      setCredit(c);
      setInvoices(inv.invoices || []);
    } catch (e) { setError(e.message); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Back from Stripe: check how the payment ended.
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const invoice = q.get('invoice');
    const session = q.get('session_id');
    if (q.get('cancelled') === 'true') {
      setNotice({ type: 'err', text: 'Payment cancelled. The invoice is still open.' });
      return;
    }
    if (!invoice || !session) return;
    let tries = 0;
    let stop = false;
    const check = async () => {
      if (stop) return;
      tries += 1;
      try {
        const r = await fetch(`${API}/webship/credit/invoices/${invoice}/pay/status/${session}`, { headers: authHeaders(token) });
        const d = await r.json();
        if (!r.ok) throw new Error(apiErrorMessage(d, 'Could not check the payment.'));
        if (d.problem) {
          setNotice({ type: 'err', text: 'The payment came back with a different amount. The office has been alerted and will contact you.' });
          return;
        }
        if (d.processing && d.invoice_status !== 'paid') {
          // ACH: el banco confirma en unos dias. La factura no se congela mientras tanto.
          setNotice({ type: 'ok', text: 'Your bank payment is being processed. The invoice shows as paid when the bank confirms, usually in 3 to 5 business days, and your credit is not put on hold meanwhile.' });
          window.history.replaceState({}, '', window.location.pathname);
          load();
          return;
        }
        if (d.invoice_status === 'paid') {
          setNotice({ type: 'ok', text: 'Payment received. Your invoice is paid.' });
          window.history.replaceState({}, '', window.location.pathname);
          load();
          return;
        }
        if (tries < 8) setTimeout(check, 2500);
        else setNotice({ type: 'err', text: 'Your payment is still being confirmed. Refresh in a minute.' });
      } catch (e) { setNotice({ type: 'err', text: e.message }); }
    };
    check();
    return () => { stop = true; };
  }, [token, load]);

  // Back from saving a payment method in Stripe (Mike, 9-oct: "Auto charge the bills").
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const sesion = q.get('autopay_session');
    if (!sesion) return;
    (async () => {
      try {
        const r = await fetch(`${API}/webship/credit/autopay/confirm`, {
          method: 'POST', headers: authHeaders(token), body: JSON.stringify({ session_id: sesion }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(apiErrorMessage(d, 'The payment method was not saved.'));
        setNotice({ type: 'ok', text: `Automatic payment is on: ${d.label}. Each weekly invoice is charged to it when it is issued.` });
      } catch (e) { setNotice({ type: 'err', text: e.message }); }
      window.history.replaceState({}, '', window.location.pathname);
      load();
    })();
  }, [token, load]);

  const guardarMedio = async () => {
    setAutopayBusy(true); setNotice(null);
    try {
      const r = await fetch(`${API}/webship/credit/autopay/setup`, {
        method: 'POST', headers: authHeaders(token), body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(apiErrorMessage(d, 'Could not open the payment page. Nothing was saved.'));
      window.location.href = d.checkout_url;
    } catch (e) { setNotice({ type: 'err', text: e.message }); setAutopayBusy(false); }
  };

  const apagarAutopay = async () => {
    setAutopayBusy(true); setNotice(null);
    try {
      const r = await fetch(`${API}/webship/credit/autopay/remove`, { method: 'POST', headers: authHeaders(token) });
      const d = await r.json();
      if (!r.ok) throw new Error(apiErrorMessage(d, 'Could not turn off automatic payment.'));
      setNotice({ type: 'ok', text: 'Automatic payment is off. Pay each invoice from this page.' });
      load();
    } catch (e) { setNotice({ type: 'err', text: e.message }); }
    finally { setAutopayBusy(false); }
  };

  const pay = async (inv) => {
    setPaying(inv.id); setNotice(null);
    try {
      const r = await fetch(`${API}/webship/credit/invoices/${inv.id}/pay`, {
        method: 'POST', headers: authHeaders(token),
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(apiErrorMessage(d, 'Could not start the payment. Nothing was charged.'));
      window.location.href = d.checkout_url;
    } catch (e) { setNotice({ type: 'err', text: e.message }); setPaying(null); }
  };

  if (error) {
    return (
      <div className="p-4 rounded-xl bg-rose-50 text-rose-700 text-sm flex items-start gap-2" data-testid="credit-error">
        <AlertTriangle className="w-4 h-4 mt-0.5" /><span>{error}</span>
      </div>
    );
  }
  if (!credit) {
    return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-amber-500" /></div>;
  }

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 space-y-4" data-testid="credit-card">
      <div className="flex items-center gap-2">
        <CreditCard className="w-5 h-5 text-amber-500" />
        <h3 className="text-lg font-semibold">Your SelleGard credit</h3>
      </div>

      {notice && (
        <div className={`p-3 rounded-lg text-sm flex items-start gap-2 ${notice.type === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`} data-testid="credit-notice">
          {notice.type === 'ok' ? <CheckCircle2 className="w-4 h-4 mt-0.5" /> : <AlertTriangle className="w-4 h-4 mt-0.5" />}
          <span>{notice.text}</span>
        </div>
      )}

      {!credit.authorized ? (
        <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 text-sm" data-testid="credit-not-authorized">
          <p className="font-medium">SelleGard shipping runs on credit for authorized merchants.</p>
          <p className="text-slate-600 mt-1">Call the office at {OFFICE_PHONE} to request it. Until then, labels cannot be booked here.</p>
        </div>
      ) : (
        <>
          {!credit.kys_approved && (
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 text-sm" data-testid="credit-kys-required">
              Your KYS verification must be approved to ship on credit. Complete it or call the office at {OFFICE_PHONE}.
            </div>
          )}
          {credit.frozen && (
            <div className="p-4 rounded-lg bg-rose-50 dark:bg-rose-950/30 text-rose-700 text-sm flex items-start gap-2" data-testid="credit-frozen">
              <Lock className="w-4 h-4 mt-0.5" />
              <span>Your credit is on hold: {credit.frozen_reason_text}. Pay the open invoice or call the office at {OFFICE_PHONE}. New shipments cannot be booked until then.</span>
            </div>
          )}
          <div className="grid grid-cols-3 gap-4">
            <div><p className="text-xs uppercase tracking-wide text-slate-500">Credit limit</p><p className="text-2xl font-bold tabular-nums" data-testid="credit-limit">{money(credit.limit)}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-slate-500">Owed</p><p className="text-2xl font-bold tabular-nums text-amber-700" data-testid="credit-owed">{money(credit.outstanding)}</p></div>
            <div><p className="text-xs uppercase tracking-wide text-slate-500">Available</p><p className={`text-2xl font-bold tabular-nums ${credit.over_limit ? 'text-rose-600' : 'text-emerald-600'}`} data-testid="credit-available">{money(credit.available)}</p></div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 text-sm" data-testid="autopay">
            <div className="flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-slate-500" />
              {credit.autopay?.enabled ? (
                <span>Automatic payment: <b>{credit.autopay.label}</b>. Each weekly invoice is charged to it when it is issued.</span>
              ) : (
                <span>Save a card or bank account and each weekly invoice is paid automatically.</span>
              )}
            </div>
            {credit.autopay?.enabled ? (
              <Button size="sm" variant="outline" onClick={apagarAutopay} disabled={autopayBusy} data-testid="autopay-off">
                {autopayBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Turn off'}
              </Button>
            ) : (
              <Button size="sm" onClick={guardarMedio} disabled={autopayBusy} data-testid="autopay-on">
                {autopayBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Set up automatic payment'}
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-500">
            Invoiced every {credit.billing_weekday_name} with every shipment itemized. Pay it in full within {credit.grace_days} days or the credit goes on hold. USPS is always prepaid: buy those labels in Rate Shop with your wallet.
          </p>
        </>
      )}

      {invoices.length > 0 && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden" data-testid="credit-invoices">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-900/60 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Invoice</th>
                <th className="px-3 py-2 text-left">Issued</th>
                <th className="px-3 py-2 text-left">Due</th>
                <th className="px-3 py-2 text-right">Shipments</th>
                <th className="px-3 py-2 text-right">Total</th>
                <th className="px-3 py-2 text-right" />
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-t border-slate-100 dark:border-slate-800" data-testid={`invoice-${inv.number}`}>
                  <td className="px-3 py-2 font-mono text-xs flex items-center gap-1.5"><Receipt className="w-3.5 h-3.5 text-slate-400" />{inv.number}</td>
                  <td className="px-3 py-2">{day(inv.issued_at)}</td>
                  <td className="px-3 py-2">{day(inv.due_at)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{inv.shipments}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{money(inv.total)}</td>
                  <td className="px-3 py-2 text-right">
                    {inv.status === 'paid' ? (
                      <span className="text-xs text-emerald-600">Paid {day(inv.paid_at)}</span>
                    ) : inv.payment_processing ? (
                      <span className="text-xs text-amber-700" data-testid={`processing-${inv.number}`}>Bank payment processing</span>
                    ) : (
                      <Button size="sm" onClick={() => pay(inv)} disabled={paying === inv.id} data-testid={`pay-${inv.number}`}>
                        {paying === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Pay'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
